import React, { useEffect, useMemo, isValidElement } from 'react';
import { createPortal } from 'react-dom';
import { ManagementHeader } from '@/components/ManagementHeader';
import { useOptionalPageHeaderContext } from './PageHeaderContext';
import { useUnifiedConsumerNav } from '@/hooks/useUnifiedConsumerNav';
import { useOrgSlug } from '@/hooks/useOrgSlug';
import type { DashboardBreadcrumb } from '@/lib/dashboardBreadcrumbs';

export interface PageHeaderProps {
  title: string;
  /** Optional control rendered beside the in-card page title. */
  titleAccessory?: React.ReactNode;
  /** Visible subtitle in the in-card management header. */
  subtitle?: React.ReactNode;
  /** Optional extra help shown in the top-bar Help popover. */
  help?: React.ReactNode;
  /** Optional DOM id for the top-bar Page help trigger (guided tours). */
  helpAnchorId?: string;
  /** Primary page actions — rendered in the in-card management header. */
  actions?: React.ReactNode;
  /** Secondary toolbar — filters, navigation, counts below the title row. */
  subheader?: React.ReactNode;
  /** Optional nested breadcrumb trail (Home is prepended automatically). */
  breadcrumbs?: DashboardBreadcrumb[];
}

function resolveSubtitle(subtitle: React.ReactNode | undefined, help: React.ReactNode | undefined) {
  if (subtitle !== undefined && subtitle !== null && subtitle !== '') {
    return subtitle;
  }
  if (!help) {
    return null;
  }
  if (typeof help === 'string' && help.trim()) {
    return help;
  }
  if (isValidElement<{ children?: React.ReactNode }>(help) && help.type === 'p') {
    const child = help.props.children;
    if (typeof child === 'string' && child.trim()) {
      return child;
    }
  }
  return null;
}

function UnifiedConsumerPageHeaderExtras({
  actions,
  subheader,
}: Pick<PageHeaderProps, 'actions' | 'subheader'>) {
  if (!actions && !subheader) return null;
  return (
    <div className="mb-4 space-y-4">
      {actions ? <div className="flex flex-wrap items-center justify-end gap-2">{actions}</div> : null}
      {subheader}
    </div>
  );
}

function DashboardPageHeader({
  title,
  titleAccessory,
  subtitle,
  help,
  helpAnchorId,
  actions,
  subheader,
  breadcrumbs,
}: PageHeaderProps) {
  const ctx = useOptionalPageHeaderContext();
  const setHeader = ctx?.setHeader;
  const clearHeader = ctx?.clearHeader;
  const inCardHeaderSlotEl = ctx?.inCardHeaderSlotEl;
  const resolvedSubtitle = useMemo(() => resolveSubtitle(subtitle, help), [subtitle, help]);

  useEffect(() => {
    if (!setHeader || !clearHeader) return undefined;
    setHeader({
      title,
      titleAccessory: titleAccessory ?? null,
      subtitle: resolvedSubtitle,
      help: help ?? null,
      helpAnchorId: helpAnchorId ?? null,
      breadcrumbs: breadcrumbs ?? null,
    });
    return () => clearHeader();
  }, [title, titleAccessory, resolvedSubtitle, help, helpAnchorId, breadcrumbs, setHeader, clearHeader]);

  if (!ctx) {
    return <UnifiedConsumerPageHeaderExtras actions={actions} subheader={subheader} />;
  }

  if (!inCardHeaderSlotEl || !title) {
    return null;
  }

  return createPortal(
    <ManagementHeader
      title={title}
      titleAccessory={titleAccessory}
      subtitle={resolvedSubtitle}
      actions={actions}
      subheader={subheader}
    />,
    inCardHeaderSlotEl
  );
}

/**
 * Registers page metadata with the dashboard shell and renders a flush in-card ManagementHeader.
 * In the unified public org member portal, titles come from PublicOrgMemberPage — only actions/subheader render here.
 */
export function PageHeader(props: PageHeaderProps) {
  const { useUnifiedNav } = useUnifiedConsumerNav();
  const orgSlug = useOrgSlug();

  // Unified org portal uses PublicOrgMemberPage for titles; orgSlug covers the brief store sync window.
  if (useUnifiedNav || orgSlug) {
    return <UnifiedConsumerPageHeaderExtras actions={props.actions} subheader={props.subheader} />;
  }

  return <DashboardPageHeader {...props} />;
}
